import { existsSync } from 'fs'
import path from 'path'
import fs, { pathExists } from 'fs-extra'

export async function getProjectConfig(targetDir: string) {
  const projectInfo = await getProjectInfo(targetDir)

  if (!projectInfo) return null

  const prefix = projectInfo.srcDir ? 'src/' : ''

  return {
    $schema: 'https://ui.shadcn.com/schema.json',
    style: 'default',
    rsc: projectInfo.appDir,
    tsx: projectInfo.tsx,
    tailwind: {
      config: projectInfo.tailwindConfig,
      css: projectInfo.appDir
        ? `${prefix}app/globals.css`
        : `${prefix}styles/globals.css`,
      // See https://ui.shadcn.com/docs/components-json#tailwindbasecolor.
      baseColor: 'gray',
      cssVariables: true,
    },
    aliases: {
      utils: `${projectInfo.pathPrefix}/lib/utils`,
      components: `${projectInfo.pathPrefix}/components`,
    },
  }
}

export async function getProjectInfo(targetDir: string) {
  // We'll only handle next.js projects for now.
  const isNext =
    existsSync(path.resolve(targetDir, 'next.config.js')) ||
    existsSync(path.resolve(targetDir, 'next.config.ts')) ||
    existsSync(path.resolve(targetDir, 'next.config.mjs'))

  if (!isNext) return null

  // We assume a TypeScript project.
  const tsconfig = await getTsConfig(targetDir)
  if (!tsconfig) return null

  // Check if using the src dir.
  const srcDir = existsSync(path.resolve(targetDir, 'src'))

  // Check if using app dir.
  const appDir = existsSync(path.resolve(targetDir, srcDir ? 'src/app' : 'app'))

  const paths = tsconfig.compilerOptions?.paths

  // Extract the prefix from the paths.
  const pathPrefix = Object.keys(paths)
    .find(key => key.endsWith('/*'))
    ?.slice(0, -2)

  return {
    srcDir,
    appDir,
    pathPrefix,
    tsx: await isTypeScriptProject(targetDir),
    tailwindConfig: existsSync(path.resolve(targetDir, 'tailwind.config.ts'))
      ? 'tailwind.config.ts'
      : 'tailwind.config.js',
  }
}

export async function isTypeScriptProject(targetDir: string) {
  return await pathExists(path.resolve(targetDir, 'tsconfig.json'))
}

export async function getTsConfig(targetDir: string) {
  try {
    const isTsx = await isTypeScriptProject(targetDir)

    if (isTsx) {
      const tsconfigPath = path.resolve(targetDir, 'tsconfig.json')
      const tsconfig = await fs.readJSON(tsconfigPath)

      if (!tsconfig) {
        throw new Error('tsconfig.json is missing')
      }

      return tsconfig
    }

    // Try jsconfig.json.
    const jsconfigPath = path.resolve(targetDir, 'jsconfig.json')
    if (jsconfigPath) {
      const jsconfig = await fs.readJSON(jsconfigPath)

      if (!jsconfig) {
        throw new Error('jsconfig.json is missing')
      }

      return jsconfig
    }
  } catch (error) {
    return null
  }
}
